import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatDistributionCenterName } from '@/utils/formatters'
import { daysDiffFor, type PogDetailRow } from './pogOnTimeProjectionMockData'

interface Props {
  rows: PogDetailRow[]
  /**
   * When set, used as the footer's "Total" Days Diff instead of a real sum
   * over `rows` — see `ANCHOR_DETAIL_TOTAL_DAYS_DIFF`'s comment in
   * pogOnTimeProjectionMockData.ts for why the reference screenshot's own
   * total (266) can't be reproduced from just the visible rows.
   */
  totalOverride?: number
}

function formatFullDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  })
}

export function POGDetailsTable({ rows, totalOverride }: Props) {
  // Real sum over whatever rows currently pass the active filters, unless
  // the caller supplied the anchored screenshot total for the default
  // ("All" filters) state — see `totalOverride`'s doc comment above.
  const totalDaysDiff =
    totalOverride ?? rows.reduce((sum, r) => sum + daysDiffFor(r), 0)

  return (
    <div className="max-h-[640px] overflow-auto rounded-lg border border-border bg-white shadow-sm">
      <Table>
        <TableHeader className="sticky top-0 z-10 bg-white">
          <TableRow className="hover:bg-transparent">
            <TableHead>POG Group</TableHead>
            <TableHead>Event</TableHead>
            <TableHead>DC</TableHead>
            <TableHead>PO</TableHead>
            <TableHead>Vendor</TableHead>
            <TableHead>SKU</TableHead>
            <TableHead>In Store Date</TableHead>
            <TableHead>Set Date</TableHead>
            <TableHead className="text-right">Days Diff</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.id}>
              <TableCell>{row.pogGroup}</TableCell>
              <TableCell>{row.event}</TableCell>
              <TableCell>{formatDistributionCenterName(row.dc)}</TableCell>
              <TableCell>{row.po}</TableCell>
              <TableCell>{row.vendor}</TableCell>
              <TableCell>{row.sku}</TableCell>
              <TableCell className="tabular-nums">
                {formatFullDate(row.inStoreDate)}
              </TableCell>
              <TableCell className="tabular-nums">
                {formatFullDate(row.setDate)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {daysDiffFor(row)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell className="font-bold">Total</TableCell>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell className="text-right tabular-nums font-semibold">
              {totalDaysDiff}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  )
}
